/*
  This example requires some changes to your config:
  
  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ],
  }
  ```
*/
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import { toast } from "react-toastify";
import AdminSidebar from "../../../components/AdminSidebar";
import Joi from "joi";

export default function UserForm() {
  let { userId } = useParams();
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    last_name: "",
    username: "",
    password: "",
    role: "",
    user_id: "",
  });
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    if (userId) {
      fetchUser();
    }
  }, [userId]);

  const fetchUser = async () => {
    try {
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/users/${userId}`
      );
      setFormData({
        name: response.data.user.name,
        last_name: response.data.user.last_name,
        username: response.data.user.username,
        role: response.data.user.role,
        user_id: response.data.user.user_id,
      });
      if (response.data.user.role === "SuperAdmin") {
        navigate("/admin/users");
        toast.error("SuperAdmin cannot be edited");
        return;
      }
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    // Clear error for the field being changed
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateFormData = () => {
    const schema = Joi.object({
      name: Joi.string().min(2).required().messages({
        "string.empty": "First name is required",
        "string.min": "First name must be at least 2 characters long",
      }),
      last_name: Joi.string().min(2).required().messages({
        "string.empty": "Last name is required",
        "string.min": "Last name must be at least 2 characters long",
      }),
      username: Joi.string().min(3).required().messages({
        "string.empty": "Username is required",
        "string.min": "Username must be at least 3 characters long",
      }),
      password: userId
        ? Joi.string().min(6).allow("").optional().messages({
            "string.min": "Password must be at least 6 characters long",
          })
        : Joi.string().min(6).required().messages({
            "string.empty": "Password is required",
            "string.min": "Password must be at least 6 characters long",
          }),
      role: Joi.string()
        .valid("Admin", "SuperAdmin", "Technician", "Stock Manager")
        .required()
        .messages({
          "any.only": "Please select a valid role",
          "string.empty": "Role is required",
        }),
      user_id: Joi.alternatives()
        .try(Joi.string(), Joi.number())
        .required()
        .messages({
          "any.required": "User ID is required",
          "alternatives.types": "User ID must be a string or number",
        }),
    });

    const { error } = schema.validate(formData, { abortEarly: false });
    if (error) {
      const newErrors = {};
      error.details.forEach((detail) => {
        newErrors[detail.path[0]] = detail.message;
      });
      setErrors(newErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateFormData()) {
      toast.error("Please correct the errors in the form.");
      return;
    }

    try {
      const dataToSend = { ...formData };
      // If editing and password is empty, remove it from the request
      if (userId && !dataToSend.password) {
        delete dataToSend.password;
      }

      if (userId) {
        await axiosInstance.put(
          `${window.REACT_APP_API_ENDPOINT}/users/${userId}`,
          dataToSend
        );
      } else {
        await axiosInstance.post(
          window.REACT_APP_API_ENDPOINT + "/users",
          dataToSend
        );
      }
      toast.success("User saved successfully!");
      navigate("/admin/users");
    } catch (error) {
      toast.error(`Failed to save user. ${error.response.data.error}`);
      console.error("Product save error:", error);
    }
  };

  const inputClassName = (fieldName) => `
    block w-full rounded-md border-0 py-3 px-4 text-gray-900 shadow-sm ring-1 ring-inset 
    ${
      errors[fieldName]
        ? "ring-red-300 focus:ring-red-500"
        : "ring-gray-300 focus:ring-indigo-600"
    } 
    placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6
  `;

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8">
        <form className="w-full lg:w-1/2 space-y-6" onSubmit={handleFormSubmit}>
          <div>
            <label
              htmlFor="user_id"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              User ID
            </label>
            <div className="relative mt-2">
              <input
                id="user_id"
                name="user_id"
                type="text"
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                placeholder="User ID"
                value={formData.user_id}
                onChange={handleFormChange}
                className={inputClassName("user_id")}
              />
              {errors.user_id && (
                <p className="mt-2 text-sm text-red-600">{errors.user_id}</p>
              )}
            </div>
          </div>
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              First Name
            </label>
            <div className="relative mt-2">
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                placeholder="First Name"
                value={formData.name}
                onChange={handleFormChange}
                className={inputClassName("name")}
              />
              {errors.name && (
                <p className="mt-2 text-sm text-red-600">{errors.name}</p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="last_name"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Last Name
            </label>
            <div className="relative mt-2">
              <input
                type="text"
                name="last_name"
                id="last_name"
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                className={inputClassName("last_name")}
                placeholder="Smith"
                value={formData.last_name}
                onChange={handleFormChange}
              />
              {errors.last_name && (
                <p className="mt-2 text-sm text-red-600">{errors.last_name}</p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="username"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Username
            </label>
            <div className="relative mt-2">
              <input
                type="text"
                name="username"
                id="username"
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                className={inputClassName("username")}
                placeholder="Username"
                value={formData.username}
                onChange={handleFormChange}
              />
              {errors.username && (
                <p className="mt-2 text-sm text-red-600">{errors.username}</p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Password{" "}
              {userId && <span className="text-gray-500">(Optional)</span>}
            </label>
            <div className="relative mt-2">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                id="password"
                autoComplete="new-password"
                autoCorrect="off"
                spellCheck="false"
                className={inputClassName("password")}
                placeholder={
                  userId ? "Leave blank to keep current password" : "Password"
                }
                value={formData.password}
                onChange={handleFormChange}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                )}
              </button>
              {errors.password && (
                <p className="mt-2 text-sm text-red-600">{errors.password}</p>
              )}
            </div>
          </div>
          {}
          <div>
            <label className="text-base font-semibold text-gray-900">
              Role
            </label>
            <fieldset className="mt-4">
              <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                <div key="admin" className="flex items-center">
                  <input
                    id="admin"
                    name="role"
                    type="radio"
                    checked={formData.role === "Admin"}
                    value="Admin"
                    onChange={handleFormChange}
                    className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                  <label
                    htmlFor="admin"
                    className="ml-3 block text-sm font-medium leading-6 text-gray-900"
                  >
                    Admin
                  </label>
                </div>

                <div key="technician" className="flex items-center">
                  <input
                    id="technician"
                    name="role"
                    type="radio"
                    checked={formData.role === "Technician"}
                    value="Technician"
                    onChange={handleFormChange}
                    className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                  <label
                    htmlFor="technician"
                    className="ml-3 block text-sm font-medium leading-6 text-gray-900"
                  >
                    Technician
                  </label>
                </div>
                <div key="stock-manager" className="flex items-center">
                  <input
                    id="stock-manager"
                    name="role"
                    type="radio"
                    checked={formData.role === "Stock Manager"}
                    value="Stock Manager"
                    onChange={handleFormChange}
                    className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                  <label
                    htmlFor="stock-manager"
                    className="ml-3 block text-sm font-medium leading-6 text-gray-900"
                  >
                    Stock Manager
                  </label>
                </div>
              </div>
              {errors.role && (
                <p className="mt-2 text-sm text-red-600">{errors.role}</p>
              )}
            </fieldset>
          </div>

          <div>
            <button
              type="submit"
              className="rounded-md bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-full sm:w-auto"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </AdminSidebar>
  );
}
